import { useContext } from "react";
import styled from "styled-components";
import css from "@styled-system/css";

import { DatePickerContext } from "./DatePickerContext";
import { CalendarLayout } from "./datePicker.type";

import Button from "../Button/Button";

interface DatePickerCalendarItemProps {
  onClick: () => void;
  isCurrent?: boolean;
  isHidden?: boolean;
  isSelected?: boolean;
  isInRange?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  children?: number | string;
  disabled?: boolean;
}

const DatePickerCalendarItemWrap = styled.span<{
  isInRange?: boolean;
  isHidden?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 14.2857%;

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 0;
  }
  ${(props) =>
    css({
      marginY: 1,
      ":after": {
        width: props.isInRange && !props.isHidden ? "100%" : 0,
        backgroundColor:
          props.isInRange && !props.isHidden
            ? "msg-blue-border"
            : "transparent",
        ...(props.isInRange &&
          !props.isHidden &&
          props.isRangeStart && {
            width: "85%",
            right: 0,
            left: "auto",
            borderTopLeftRadius: "50%",
            borderBottomLeftRadius: "50%",
          }),
        ...(props.isInRange &&
          !props.isHidden &&
          props.isRangeEnd && {
            width: "85%",
            borderTopRightRadius: "50%",
            borderBottomRightRadius: "50%",
          }),
      },
    })};
`;

const DatePickerCalendarItemStyled = styled(Button)<{
  calendarLayout: CalendarLayout;
  isSelected?: boolean;
  isCurrent?: boolean;
  isHidden?: boolean;
}>`
  position: relative;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
  opacity: ${(props) => (props.isHidden ? "0 !important" : "1")};
  ${(props) =>
    css({
      flex: props.calendarLayout === "days" ? "14.2857%" : "33.3333%",
      height: props.calendarLayout === "days" ? "32px" : "46px",
      width: props.calendarLayout === "days" ? "36px" : "46px",
      maxWidth: props.calendarLayout === "days" ? "32px" : "46px",
      marginX: props.calendarLayout === "days" ? 1 : 4,
      marginY: 0,
      padding: 0,
      fontSize: 1,
      fontWeight: 1,
      color: props.isSelected ? "primary-white" : "black",
      border: !props.isSelected
        ? props.isCurrent
          ? "2px solid #f1f1f1"
          : "2px solid transparent"
        : "none",
      "&:not(:disabled):hover": {
        borderColor: props.isSelected ? "primary-white" : "msg-blue-border",
      },
    })};
`;

export default function DatePickerCalendarItem({
  isCurrent,
  isHidden,
  isSelected,
  isInRange,
  isRangeStart,
  isRangeEnd,
  children,
  disabled,
  onClick,
}: DatePickerCalendarItemProps) {
  const { calendarLayout } = useContext(DatePickerContext);
  const variant = isSelected ? "primary" : "secondary-white";
  return (
    <DatePickerCalendarItemWrap
      isInRange={isInRange}
      isHidden={isHidden}
      isRangeStart={isRangeStart}
      isRangeEnd={isRangeEnd}
    >
      <DatePickerCalendarItemStyled
        variant={variant}
        isCurrent={isCurrent}
        isSelected={isSelected}
        isHidden={isHidden}
        calendarLayout={calendarLayout}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </DatePickerCalendarItemStyled>
    </DatePickerCalendarItemWrap>
  );
}
